// webapp/src/components/feedback/FeedbackWizard.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  makeStyles,
  tokens,
  Button,
  Textarea,
  Spinner,
  Link,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  DismissRegular,
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';

import {
  collectEnvironment,
  captureScreenshots,
  submitFeedback,
  buildWizardPayload,
} from '../../services/feedbackService';
import type {
  FeedbackScreenshots,
  FeedbackEnvironment,
  WizardState,
  SurveyContext,
} from '../../services/feedbackService';

import {
  BUG_STEPS,
  FEATURE_STEPS,
  getSmartPlaceholder,
  getTotalSteps,
} from './FeedbackWizardSteps';
import type { WizardStepDef } from './FeedbackWizardSteps';
import { FeedbackReviewStep } from './FeedbackReviewStep';

export type FeedbackType = 'bug' | 'feature';

interface FeedbackWizardProps {
  type: FeedbackType;
  onClose: () => void;
  surveyContext?: SurveyContext;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  surface: {
    maxWidth: '600px',
    width: '90vw',
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minHeight: '200px',
  },
  stepTitle: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '24px',
    margin: 0,
    color: tokens.colorNeutralForeground1,
  },
  textarea: {
    minHeight: '120px',
  },
  progressContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 0',
  },
  progressDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    cursor: 'default',
    transition: 'background-color 0.2s',
  },
  progressDotActive: {
    backgroundColor: tokens.colorBrandBackground,
  },
  progressDotInactive: {
    backgroundColor: tokens.colorNeutralStroke1,
  },
  progressDotCompleted: {
    backgroundColor: tokens.colorBrandBackground,
    opacity: 0.6,
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
  },
  actionsLeft: {
    display: 'flex',
    gap: '8px',
  },
  actionsRight: {
    display: 'flex',
    gap: '8px',
    marginLeft: 'auto',
  },
  navButton: {
    minHeight: '44px',
    minWidth: '44px',
  },
  // Severity picker
  severityContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  severityCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    borderRadius: '8px',
    border: `2px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    minHeight: '44px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  severityCardSelected: {
    border: `2px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  severityLabel: {
    fontWeight: 600,
    fontSize: '15px',
  },
  severityDescription: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  successBox: {
    textAlign: 'center',
    padding: '24px',
  },
  confirmContent: {
    padding: '8px 0',
  },
});

// ---------------------------------------------------------------------------
// Severity options
// ---------------------------------------------------------------------------

interface SeverityOption {
  value: 'critical' | 'medium' | 'low';
  label: string;
  description: string;
  emoji: string;
}

const SEVERITY_OPTIONS: SeverityOption[] = [
  {
    value: 'critical',
    label: 'Blocks my work',
    description: "I can't continue until this is fixed",
    emoji: '\u{1F6A8}',
  },
  {
    value: 'medium',
    label: 'Annoying but I can work around it',
    description: "It slows me down but there's a workaround",
    emoji: '\u{26A0}\u{FE0F}',
  },
  {
    value: 'low',
    label: 'Minor cosmetic issue',
    description: "Something looks off but doesn't affect my work",
    emoji: '\u{1F4DD}',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackWizard({ type, onClose, surveyContext }: FeedbackWizardProps) {
  const styles = useStyles();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const { accounts } = useMsal();

  // Wizard state
  const [wizardState, setWizardState] = useState<WizardState>({ type });
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Reporter identity — prefilled from the signed-in account but editable on
  // the review step (BUG C: users must be able to paste an email address).
  const initialReporter = accounts[0];
  const [reporterName, setReporterName] = useState(initialReporter?.name ?? 'Unknown');
  const [reporterEmail, setReporterEmail] = useState(initialReporter?.username ?? '');

  // Capture state
  const [screenshots, setScreenshots] = useState<FeedbackScreenshots>({ viewport: null, canvas: null });
  const [environment, setEnvironment] = useState<FeedbackEnvironment | null>(null);
  const [capturing, setCapturing] = useState(true);

  // Submit state
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [issueUrl, setIssueUrl] = useState('');
  const [issueNumber, setIssueNumber] = useState(0);
  const [gheMirrorFailed, setGheMirrorFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const totalSteps = getTotalSteps(type);
  const textSteps = type === 'bug' ? BUG_STEPS : FEATURE_STEPS;
  // Bug: steps 0-2 are text, 3 is severity, 4 is review
  // Feature: steps 0-2 are text, 3 is review
  const isSeverityStep = type === 'bug' && currentStep === 3;
  const isReviewStep = currentStep === totalSteps - 1;
  const isTextStep = !isSeverityStep && !isReviewStep;
  const currentTextStep: WizardStepDef | undefined = isTextStep ? textSteps[currentStep] : undefined;

  // ---------------------------------------------------------------------------
  // Auto-capture on mount
  // ---------------------------------------------------------------------------

  // Capture args are read through a ref so auto-capture runs exactly once on
  // mount: `surveyContext` arrives as a fresh object identity whenever the
  // survey page's effect re-runs, and re-firing a full-viewport html2canvas
  // capture on every identity change disrupts the open form (BUG B).
  const captureArgsRef = useRef({ pathname: location.pathname, projectId: params.id, surveyContext });
  captureArgsRef.current = { pathname: location.pathname, projectId: params.id, surveyContext };

  const runCapture = useCallback(async () => {
    const { pathname, projectId, surveyContext: ctx } = captureArgsRef.current;
    setCapturing(true);
    try {
      const [shots, env] = await Promise.all([
        captureScreenshots(),
        Promise.resolve(
          collectEnvironment(
            pathname,
            ctx?.projectName,
            projectId,
            ctx,
          ),
        ),
      ]);
      // Merge instead of replace: on iPad a full-viewport html2canvas capture
      // can fail (memory pressure) and return null — wholesale replacement
      // then silently wiped screenshots the user was already attaching (BUG B:
      // "the issue disappears").
      setScreenshots(prev => ({
        viewport: shots.viewport ?? prev.viewport,
        canvas: shots.canvas ?? prev.canvas,
      }));
      setEnvironment(env);
    } finally {
      setCapturing(false);
    }
  }, []);

  useEffect(() => {
    void runCapture();
  }, [runCapture]);

  // ---------------------------------------------------------------------------
  // Cooldown timer
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // ---------------------------------------------------------------------------
  // State helpers
  // ---------------------------------------------------------------------------

  const updateField = useCallback((fieldKey: keyof WizardState, value: string) => {
    setWizardState(prev => ({ ...prev, [fieldKey]: value }));
  }, []);

  // BUG A — attach an image already on the device as the bug screenshot. The
  // attached image takes the viewport slot: the API posts it through the
  // existing screenshot pipeline (GHE_ISSUE_LIMITS.maxScreenshotLength is
  // enforced client-side by readImageFileAsBase64's downscale).
  const handleAttachImage = useCallback((base64: string) => {
    setScreenshots(prev => ({ ...prev, viewport: base64 }));
  }, []);

  const hasAnyContent = (): boolean => {
    const excludedKeys: Array<keyof WizardState> = ['type', 'customTitle'];
    return Object.entries(wizardState).some(
      ([key, v]) =>
        !excludedKeys.includes(key as keyof WizardState) &&
        typeof v === 'string' &&
        v.trim().length > 0
    );
  };

  const isCurrentStepValid = (): boolean => {
    if (isSeverityStep) {
      return !!wizardState.severity;
    }
    if (isReviewStep) {
      return true;
    }
    if (currentTextStep) {
      if (currentTextStep.isOptional) return true;
      const value = wizardState[currentTextStep.fieldKey] as string | undefined;
      return !!value && value.trim().length > 0;
    }
    return false;
  };

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleGoToStep = (step: number) => {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step);
    }
  };

  const handleClose = () => {
    if (submitState === 'success' || !hasAnyContent()) {
      onClose();
    } else {
      setShowConfirmClose(true);
    }
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!environment) return;

    const reporter = {
      name: reporterName.trim() || 'Unknown',
      email: reporterEmail.trim(),
    };

    setSubmitState('submitting');
    setErrorMessage('');

    try {
      const payload = buildWizardPayload(wizardState, environment, reporter, screenshots);
      const result = await submitFeedback(
        payload.type,
        payload.userInput,
        payload.environment,
        payload.reporter,
        payload.screenshots,
      );
      setIssueUrl(result.issueUrl);
      setIssueNumber(result.issueNumber);
      setGheMirrorFailed(result.gheMirrorFailed ?? false);
      setSubmitState('success');
      setCooldown(30);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred.');
      setSubmitState('error');
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderProgressDots = () => (
    <div className={styles.progressContainer}>
      {Array.from({ length: totalSteps }, (_, i) => {
        let dotClass = styles.progressDotInactive;
        if (i === currentStep) dotClass = styles.progressDotActive;
        else if (i < currentStep) dotClass = styles.progressDotCompleted;

        return (
          <div
            key={i}
            data-testid="progress-dot"
            className={`${styles.progressDot} ${dotClass}`}
          />
        );
      })}
    </div>
  );

  const renderTextStep = (step: WizardStepDef) => {
    const value = (wizardState[step.fieldKey] as string) || '';
    const placeholder = getSmartPlaceholder(step, surveyContext);

    return (
      <div className={styles.stepContent}>
        <h2 className={styles.stepTitle}>{step.title}</h2>
        <Textarea
          className={styles.textarea}
          value={value}
          onChange={(_e, data) => updateField(step.fieldKey, data.value)}
          placeholder={placeholder}
          resize="vertical"
          rows={5}
        />
      </div>
    );
  };

  const renderSeverityStep = () => (
    <div className={styles.stepContent}>
      <h2 className={styles.stepTitle}>How bad is it?</h2>
      <div className={styles.severityContainer}>
        {SEVERITY_OPTIONS.map((option) => {
          const isSelected = wizardState.severity === option.value;
          return (
            <div
              key={option.value}
              role="button"
              tabIndex={0}
              className={`${styles.severityCard} ${isSelected ? styles.severityCardSelected : ''}`}
              onClick={() => setWizardState(prev => ({ ...prev, severity: option.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setWizardState(prev => ({ ...prev, severity: option.value }));
                }
              }}
              aria-pressed={isSelected}
              aria-label={option.label}
            >
              <span style={{ fontSize: '24px' }}>{option.emoji}</span>
              <div>
                <div className={styles.severityLabel}>{option.label}</div>
                <div className={styles.severityDescription}>{option.description}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className={styles.successBox}>
      <p style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
        {type === 'bug' ? 'Bug report submitted!' : 'Feature request submitted!'}
      </p>
      {gheMirrorFailed ? (
        <p>
          Saved to Sentry. The issue-tracker copy could not be created right now —
          the report is safely in Sentry and nothing was lost.
        </p>
      ) : (
        <p>
          Issue #{issueNumber} has been created.{' '}
          <Link href={issueUrl} target="_blank" rel="noopener noreferrer">
            View on GitHub
          </Link>
        </p>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const dialogTitle = type === 'bug' ? 'Report a Bug' : 'Request a Feature';

  return (
    <>
      <Dialog open={true} onOpenChange={(_e, data) => { if (!data.open) handleClose(); }}>
        <DialogSurface className={styles.surface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<DismissRegular />}
                  onClick={handleClose}
                  className={styles.navButton}
                  data-testid="wizard-close"
                />
              }
            >
              {dialogTitle}
            </DialogTitle>

            <DialogContent>
              {submitState === 'success' ? (
                renderSuccess()
              ) : (
                <>
                  {renderProgressDots()}
                  {isTextStep && currentTextStep && renderTextStep(currentTextStep)}
                  {isSeverityStep && renderSeverityStep()}
                  {isReviewStep && environment && (
                    <FeedbackReviewStep
                      wizardState={wizardState}
                      environment={environment}
                      screenshots={screenshots}
                      capturing={capturing}
                      onRecapture={() => void runCapture()}
                      onEditStep={handleGoToStep}
                      onTitleChange={(title) => setWizardState(prev => ({ ...prev, customTitle: title }))}
                      reporterName={reporterName}
                      reporterEmail={reporterEmail}
                      onReporterNameChange={setReporterName}
                      onReporterEmailChange={setReporterEmail}
                      onAttachImage={type === 'bug' ? handleAttachImage : undefined}
                      type={type}
                    />
                  )}
                  {isReviewStep && !environment && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                      <Spinner size="medium" label="Loading environment data..." />
                    </div>
                  )}

                  {/* Error message */}
                  {submitState === 'error' && (
                    <p style={{ color: 'var(--hts-error-strong)', marginTop: '8px' }}>
                      {errorMessage}
                    </p>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions>
              {submitState === 'success' ? (
                <Button appearance="primary" onClick={onClose} className={styles.navButton}>
                  Close
                </Button>
              ) : (
                <div className={styles.actionsRow}>
                  <div className={styles.actionsLeft}>
                    {currentStep > 0 && (
                      <Button
                        appearance="subtle"
                        icon={<ArrowLeftRegular />}
                        onClick={handleBack}
                        className={styles.navButton}
                        aria-label="Back"
                        data-testid="wizard-back"
                      >
                        Back
                      </Button>
                    )}
                  </div>
                  <div className={styles.actionsRight}>
                    {isTextStep && currentTextStep?.isOptional && (
                      <Button
                        appearance="subtle"
                        onClick={handleSkip}
                        className={styles.navButton}
                        aria-label="Skip"
                        data-testid="wizard-skip"
                      >
                        Skip
                      </Button>
                    )}
                    {!isReviewStep && (
                      <Button
                        appearance="primary"
                        icon={<ArrowRightRegular />}
                        iconPosition="after"
                        onClick={handleNext}
                        disabled={!isCurrentStepValid()}
                        className={styles.navButton}
                        aria-label="Next"
                        data-testid="wizard-next"
                      >
                        Next
                      </Button>
                    )}
                    {isReviewStep && (
                      <Button
                        appearance="primary"
                        icon={submitState === 'submitting' ? <Spinner size="tiny" /> : <CheckmarkRegular />}
                        onClick={() => void handleSubmit()}
                        disabled={
                          !environment ||
                          capturing ||
                          submitState === 'submitting' ||
                          cooldown > 0 ||
                          !reporterEmail.trim()
                        }
                        className={styles.navButton}
                        data-testid="wizard-submit"
                      >
                        {cooldown > 0
                          ? `Wait ${cooldown}s`
                          : submitState === 'submitting'
                          ? 'Submitting...'
                          : 'Submit'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Confirm close dialog */}
      <Dialog open={showConfirmClose} onOpenChange={(_e, data) => { if (!data.open) setShowConfirmClose(false); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Discard feedback?</DialogTitle>
            <DialogContent>
              <p className={styles.confirmContent}>
                Are you sure you want to discard your feedback? Your answers will be lost.
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setShowConfirmClose(false)}
                className={styles.navButton}
              >
                Keep editing
              </Button>
              <Button
                appearance="primary"
                onClick={() => { setShowConfirmClose(false); onClose(); }}
                className={styles.navButton}
                style={{ backgroundColor: 'var(--hts-error-bg)' }}
              >
                Discard
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

export default FeedbackWizard;
